import { useQuery } from '@tanstack/react-query';
import { checkAirdropEligibility } from '../utils/sablierApi';
import type {
  SablierEligibilityResponse,
  SablierEligibilityParams,
} from '../utils/sablierTypes';

export interface UseSablierAirdropOptions {
  /** The wallet address to check eligibility for */
  address: string;
  /** The IPFS Content Identifier (CID) for the campaign */
  cid: string;
  /** Optional base URL for the Sablier API (for self-hosted instances) */
  baseUrl?: string;
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
}

export interface UseSablierAirdropResult {
  /** The eligibility data if the address is eligible */
  data: SablierEligibilityResponse | null | undefined;
  /** Whether the address is eligible for the airdrop */
  isEligible: boolean;
  /** Whether the query is currently loading */
  isLoading: boolean;
  /** Whether the query encountered an error */
  isError: boolean;
  /** The error object if the query failed */
  error: Error | null;
  /** Refetch the eligibility data */
  refetch: () => void;
}

/**
 * React hook to check if an address is eligible for a Sablier airdrop
 *
 * Uses React Query for caching and automatic refetching.
 *
 * @param options - The hook configuration options
 * @returns Query result with eligibility data and status
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { data, isEligible, isLoading } = useSablierAirdrop({
 *     address: '0x1234...',
 *     cid: 'QmXxx...'
 *   });
 *
 *   if (isLoading) return <div>Checking eligibility...</div>;
 *   if (isEligible) return <div>Eligible for {data.amount} tokens!</div>;
 *   return <div>Not eligible</div>;
 * }
 * ```
 */
export function useSablierAirdrop(
  options: UseSablierAirdropOptions,
): UseSablierAirdropResult {
  const { address, cid, baseUrl, enabled = true } = options;

  const query = useQuery({
    queryKey: ['sablier-airdrop', address, cid, baseUrl],
    queryFn: async () => {
      const params: SablierEligibilityParams = {
        address,
        cid,
        baseUrl,
      };
      return checkAirdropEligibility(params);
    },
    enabled: enabled && !!address && !!cid,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    // Don't retry on 404 or 400 (not eligible)
    retry: (failureCount, error) => {
      if (
        error instanceof Error &&
        (error.message.includes('404') || error.message.includes('400'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  return {
    data: query.data,
    isEligible: query.data !== null && query.data !== undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
